"use client";

import { RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image from "../ui/OptimizedImage";
import Link from "next/link";

type ProfileMenuProps = {
    isSignedIn: boolean | undefined;
    isProfileOpen: boolean;
    onToggle: () => void;
    onNavigate: (href: string) => void;
    onSignOut: () => void;
    displayName: string;
    userImageUrl: string | null | undefined;
    userEmail: string | null | undefined;
    profileMenuItems: { label: string; href: string }[];
    accountHref: string;
    containerRef: RefObject<HTMLDivElement | null>;
};

export function ProfileMenu({
    isSignedIn,
    isProfileOpen,
    onToggle,
    onNavigate,
    onSignOut,
    displayName,
    userImageUrl,
    userEmail,
    profileMenuItems,
    accountHref,
    containerRef,
}: ProfileMenuProps) {
    if (!isSignedIn) {
        return (
            <Link
                href={accountHref}
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-[var(--hopp-muted)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                aria-label="Account"
            >
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c1.6-3.5 5-6 8-6s6.4 2.5 8 6" />
                </svg>
            </Link>
        );
    }

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={onToggle}
                aria-label="Account"
                aria-expanded={isProfileOpen}
                className="relative hidden h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-[var(--hopp-muted)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)] md:flex"
            >
                {userImageUrl ? (
                    <Image
                        src={userImageUrl}
                        alt={displayName}
                        fill
                        className="object-cover"
                    />
                ) : (
                    <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c1.6-3.5 5-6 8-6s6.4 2.5 8 6" />
                    </svg>
                )}
            </button>

            <AnimatePresence>
                {isProfileOpen ? (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="absolute right-0 top-full z-50 mt-3 w-60 rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)]/95 p-3 shadow-[0_20px_40px_rgba(4,8,20,0.55)] backdrop-blur"
                    >
                        <div className="flex items-center gap-3 rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-ink)]/40 p-3">
                            <div className="relative h-10 w-10 overflow-hidden rounded-full border border-[var(--hopp-panel-strong)]">
                                {userImageUrl ? (
                                    <Image
                                        src={userImageUrl}
                                        alt={displayName}
                                        fill
                                        className="object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-[var(--hopp-text)]">
                                        {displayName.slice(0, 2).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-[var(--hopp-text)]">
                                    {displayName}
                                </p>
                                <p className="truncate text-[10px] uppercase tracking-[0.25em] text-[var(--hopp-muted)]">
                                    {userEmail ?? "Account"}
                                </p>
                            </div>
                        </div>

                        <div className="mt-3 space-y-1">
                            {profileMenuItems.map((item) => (
                                <button
                                    key={item.label}
                                    type="button"
                                    onClick={() => onNavigate(item.href)}
                                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--hopp-muted)] transition hover:bg-[var(--hopp-panel-strong)] hover:text-[var(--hopp-text)]"
                                >
                                    {item.label}
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="h-3.5 w-3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>
                            ))}
                        </div>

                        <div className="mt-2 border-t border-[var(--hopp-panel-strong)] pt-2">
                            <button
                                type="button"
                                onClick={onSignOut}
                                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--hopp-muted)] transition hover:bg-[rgba(255,90,90,0.12)] hover:text-[var(--hopp-text)]"
                            >
                                Log out
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </button>
                        </div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}
